/**
 * Comments section — works for tracks and comics.
 *
 * Props:
 *   targetType  "track" or "comic"
 *   targetId    the track or comic ID
 *
 * Public viewers: read-only (form hidden, reactions disabled).
 * Signed-in users: post new comments, edit their own, react.
 * Admins: can delete any comment (server flags deleted_by_admin=True).
 *
 * Pulls `user` from AuthContext internally — no prop drilling.
 */

import { useEffect, useState } from 'react';

import { useAuth } from '../../contexts/AuthContext.jsx';
import {
  listComments as listTrackComments,
  createComment as createTrackComment,
  updateComment,
  deleteComment,
  reactToComment,
} from '../../api/tracks.js';
import {
  listComicComments,
  createComicComment,
} from '../../api/comics.js';
import ReactionBar from './ReactionBar.jsx';


function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}


function CommentAvatar({ user }) {
  if (!user) return null;

  if (user.avatar_url) {
    return (
      <div className="tt-comment__avatar">
        <img
          src={`${user.avatar_url}?v=${Date.now()}`}
          alt=""
          className="tt-comment__avatar-img"
        />
      </div>
    );
  }

  const initials = user.display_name
    ? user.display_name.slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="tt-comment__avatar tt-comment__initials">
      {initials}
    </div>
  );
}


export default function CommentsSection({ trackId, targetType = 'track', targetId }) {
  const { user } = useAuth();
  const isAuth = Boolean(user);
  const isAdmin = user?.role === 'admin';
  const canComment = isAuth && (user.role === 'admin' || user.role === 'standard');

  // Support both old trackId prop and new targetType/targetId props
  const resolvedType = targetType;
  const resolvedId = targetId ?? trackId;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [newBody, setNewBody] = useState('');
  const [posting, setPosting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');

  async function refresh() {
    try {
      const data = resolvedType === 'comic'
        ? await listComicComments(resolvedId)
        : await listTrackComments(resolvedId);
      setComments(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId, resolvedType, isAuth]);

  async function handlePost() {
    const body = newBody.trim();
    if (!body) return;
    setPosting(true);
    setError(null);
    try {
      const created = resolvedType === 'comic'
        ? await createComicComment(resolvedId, body)
        : await createTrackComment(resolvedId, body);
      setComments((prev) => [...prev, created]);
      setNewBody('');
    } catch (e) {
      setError(e.message);
    } finally {
      setPosting(false);
    }
  }

  async function handleSaveEdit(id) {
    const body = editBody.trim();
    if (!body) return;
    try {
      const updated = await updateComment(id, body);
      setComments((prev) => prev.map((c) => (c.id === id ? updated : c)));
      setEditingId(null);
      setEditBody('');
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteComment(id);
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleReact(commentId, emoji) {
    try {
      const result = await reactToComment(commentId, emoji);
      setComments((prev) =>
        prev.map((c) => {
          if (c.id !== commentId) return c;
          const my = result.active
            ? [...c.my_reactions, emoji]
            : c.my_reactions.filter((e) => e !== emoji);
          return { ...c, reactions: result.counts, my_reactions: my };
        })
      );
    } catch (e) {
      setError(e.message);
    }
  }

  const sectionLabel = resolvedType === 'comic' ? '~/comments' : '~/comments';

  return (
    <section className="tt-comments">
      <header className="tt-comments__header">
        <div className="tt-comments__label">{sectionLabel}</div>
        <div className="tt-comments__count">
          {comments.filter((c) => !c.deleted_at).length}
        </div>
      </header>

      {error && <div className="tt-error">{error}</div>}

      {loading ? (
        <div className="tt-muted">Loading comments…</div>
      ) : comments.length === 0 ? (
        <div className="tt-muted">
          No comments yet.{isAuth ? ' Be the first.' : ''}
        </div>
      ) : (
        <ol className="tt-comment-list">
          {comments.map((c) => {
            const isDeleted = Boolean(c.deleted_at);
            const isMine = user && c.user && c.user.id === user.id;
            const isEditing = editingId === c.id;

            return (
              <li
                key={c.id}
                className={`tt-comment ${isDeleted ? 'is-deleted' : ''}`}
              >
                <div className="tt-comment__meta">
                  {!isDeleted && <CommentAvatar user={c.user} />}
                  <div className="tt-comment__meta-text">
                    <span className="tt-comment__author">
                      {c.user?.display_name || 'unknown'}
                    </span>
                    <span className="tt-comment__when">
                      {formatWhen(c.created_at)}
                      {c.edited_at && !isDeleted && ' · edited'}
                      {isDeleted && (c.deleted_by_admin ? ' · removed by admin' : ' · deleted')}
                    </span>
                  </div>
                </div>

                {isEditing ? (
                  <div className="tt-comment__edit">
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={3}
                    />
                    <div className="tt-comment__actions">
                      <button
                        type="button"
                        className="tt-btn tt-btn--primary"
                        onClick={() => handleSaveEdit(c.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="tt-btn"
                        onClick={() => { setEditingId(null); setEditBody(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="tt-comment__body">{c.body}</p>
                )}

                {!isDeleted && !isEditing && (
                  <div className="tt-comment__footer">
                    <ReactionBar
                      counts={c.reactions || {}}
                      myReactions={c.my_reactions || []}
                      isAuthenticated={isAuth}
                      onToggle={(emoji) => handleReact(c.id, emoji)}
                    />
                    {(isMine || isAdmin) && (
                      <div className="tt-comment__actions">
                        {isMine && (
                          <button
                            type="button"
                            className="tt-btn tt-btn--ghost"
                            onClick={() => { setEditingId(c.id); setEditBody(c.body); }}
                          >
                            edit
                          </button>
                        )}
                        <button
                          type="button"
                          className="tt-btn tt-btn--ghost tt-btn--danger"
                          onClick={() => handleDelete(c.id)}
                        >
                          delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}

      {canComment ? (
        <div className="tt-comment-form">
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            placeholder="drop your take…"
            rows={3}
          />
          <button
            type="button"
            className="tt-btn tt-btn--primary"
            onClick={handlePost}
            disabled={posting || !newBody.trim()}
          >
            {posting ? 'posting…' : 'post comment'}
          </button>
        </div>
      ) : isAuth ? (
        <div className="tt-muted tt-comment-form__locked">
          Your account can't post comments. Ask Tim if that's wrong.
        </div>
      ) : (
        <div className="tt-muted tt-comment-form__locked">
          <a href="/login">Sign in</a> to comment and react.
        </div>
      )}
    </section>
  );
}
